import type { paths } from '@/lib/api-types-clinical-study';
import type { StudySubjectListItem } from '@/clinical-study/subjects/useStudySubjectList';
import type { TreatmentDatePoolEntry } from '@/clinical-study/subjects/useTreatmentDatePoolPicker';

export type InternalSchedule =
  paths['/v1/clinical-study/studies/{studyId}/subjects/{subjectId}/schedule']['get']['responses'][200]['content']['application/json'];
type ScheduleTimepoint = InternalSchedule['timepoints'][number];
export type VisitState = ScheduleTimepoint['lifecycleState'];

/** What the calendar needs of a subject: its row of the study's subject list. */
export type CalendarSubject = Pick<
  StudySubjectListItem,
  | 'id'
  | 'lifecycleState'
  | 'provisionalIdentifier'
  | 'definitiveIdentifier'
  | 'actualTreatmentDate'
  | 'reservedTreatmentDatePoolEntryId'
>;

type SubjectRef = { subjectId: string; subjectCode: string };
type VisitRef = SubjectRef & {
  visitId: string;
  timepointType: ScheduleTimepoint['timepointType'];
  offsetDays: number;
  state: VisitState;
  targetDate: string;
  windowStart: string;
  windowEnd: string;
  appointmentDate: string | null;
  outOfWindow: boolean | null;
};

export type CalendarItem =
  | ({ kind: 'reserved'; key: string; date: string } & SubjectRef)
  | ({ kind: 'actual'; key: string; date: string } & SubjectRef)
  | ({ kind: 'visit'; key: string; date: string } & VisitRef)
  | ({ kind: 'appointment'; key: string; date: string } & VisitRef)
  | ({ kind: 'windowStart'; key: string; date: string } & VisitRef)
  | ({ kind: 'windowEnd'; key: string; date: string } & VisitRef)
  | { kind: 'poolFree'; key: string; date: string; entryId: string; free: number };

export type CalendarItemKind = CalendarItem['kind'];

/** The order items take inside one day, and the order of the legend. */
export const CALENDAR_ITEM_KINDS = [
  'actual',
  'reserved',
  'appointment',
  'visit',
  'windowStart',
  'windowEnd',
  'poolFree',
] as const satisfies readonly CalendarItemKind[];

/** The subject's code: the definitive identifier once assigned, else the provisional one. Never
 * any personal data — the module holds none. */
export function subjectCode(subject: CalendarSubject): string {
  return subject.definitiveIdentifier ?? subject.provisionalIdentifier;
}

/** Visits whose window still matters: closed or cancelled visits show only their target. */
function windowIsOpen(state: VisitState): boolean {
  return state === 'PLANNED' || state === 'SCHEDULED';
}

export function buildCalendarItems({
  subjects,
  schedules,
  pool,
  today,
  includePool,
}: {
  subjects: readonly CalendarSubject[];
  /** By subject id; a subject without an entry contributes no visits. */
  schedules: Readonly<Record<string, InternalSchedule>>;
  /** `null` when the pool could not be read: reserved dates then cannot be resolved. */
  pool: readonly TreatmentDatePoolEntry[] | null;
  /** `YYYY-MM-DD`, Europe/Rome; free pool dates before it are not shown. */
  today: string;
  includePool: boolean;
}): CalendarItem[] {
  const items: CalendarItem[] = [];
  const poolById = new Map((pool ?? []).map((entry) => [entry.id, entry]));

  for (const subject of subjects) {
    const ref: SubjectRef = { subjectId: subject.id, subjectCode: subjectCode(subject) };
    const schedule = schedules[subject.id];
    const actualDate = subject.actualTreatmentDate ?? schedule?.actualTreatmentDate ?? null;

    if (actualDate !== null) {
      items.push({ kind: 'actual', key: `actual:${subject.id}`, date: actualDate, ...ref });
    }

    // A reservation stands for a date not yet treated; once the treatment is confirmed the actual
    // date replaces it, and a cancelled subject keeps no reservation.
    const reservedId = subject.reservedTreatmentDatePoolEntryId;
    const reservedEntry = reservedId === null ? undefined : poolById.get(reservedId);
    if (
      reservedEntry !== undefined &&
      actualDate === null &&
      subject.lifecycleState !== 'CANCELLED'
    ) {
      items.push({
        kind: 'reserved',
        key: `reserved:${subject.id}`,
        date: reservedEntry.date,
        ...ref,
      });
    }

    for (const row of schedule?.timepoints ?? []) {
      const visit: VisitRef = {
        ...ref,
        visitId: row.visitId,
        timepointType: row.timepointType,
        offsetDays: row.offsetDays,
        state: row.lifecycleState,
        targetDate: row.targetDate,
        windowStart: row.windowStart,
        windowEnd: row.windowEnd,
        appointmentDate: row.appointmentDate,
        outOfWindow: row.outOfWindowIndicator,
      };
      items.push({ kind: 'visit', key: `visit:${row.visitId}`, date: row.targetDate, ...visit });
      if (row.appointmentDate !== null && row.lifecycleState !== 'CANCELLED') {
        items.push({
          kind: 'appointment',
          key: `appointment:${row.visitId}`,
          date: row.appointmentDate,
          ...visit,
        });
      }
      if (windowIsOpen(row.lifecycleState)) {
        if (row.windowStart !== row.targetDate) {
          items.push({
            kind: 'windowStart',
            key: `windowStart:${row.visitId}`,
            date: row.windowStart,
            ...visit,
          });
        }
        if (row.windowEnd !== row.targetDate) {
          items.push({
            kind: 'windowEnd',
            key: `windowEnd:${row.visitId}`,
            date: row.windowEnd,
            ...visit,
          });
        }
      }
    }
  }

  if (includePool) {
    for (const entry of pool ?? []) {
      const free = entry.capacity - entry.consumedCount;
      if (free > 0 && entry.date >= today) {
        items.push({
          kind: 'poolFree',
          key: `pool:${entry.id}`,
          date: entry.date,
          entryId: entry.id,
          free,
        });
      }
    }
  }

  return items;
}

function itemSubjectCode(item: CalendarItem): string {
  return item.kind === 'poolFree' ? '' : item.subjectCode;
}

/** Items by day, each day in a stable order: kind first, then subject code. */
export function groupItemsByDay(items: readonly CalendarItem[]): Map<string, CalendarItem[]> {
  const byDay = new Map<string, CalendarItem[]>();
  for (const item of items) {
    const day = byDay.get(item.date);
    if (day === undefined) {
      byDay.set(item.date, [item]);
    } else {
      day.push(item);
    }
  }
  for (const day of byDay.values()) {
    day.sort(
      (left, right) =>
        CALENDAR_ITEM_KINDS.indexOf(left.kind) - CALENDAR_ITEM_KINDS.indexOf(right.kind) ||
        itemSubjectCode(left).localeCompare(itemSubjectCode(right)),
    );
  }
  return byDay;
}

/** Only `subjectId`'s items; `null` keeps everything. Pool dates belong to no subject, so a
 * subject filter hides them. */
export function filterItemsBySubject(
  items: readonly CalendarItem[],
  subjectId: string | null,
): CalendarItem[] {
  if (subjectId === null) {
    return [...items];
  }
  return items.filter((item) => item.kind !== 'poolFree' && item.subjectId === subjectId);
}
